import {
  APP_MODULE_CODES,
  NOTIFICATION_EVENT_KEYS,
  NOTIFICATION_EVENT_PRODUCT,
  type NotificationEventKey,
  NotificationProductCode,
  isNotificationEventVisible,
  type NotificationPreference,
  type OwnProfile,
} from '@gb-ticket/shared-types';
import { createContext, useContext, useState, type FormEvent, type ReactNode } from 'react';
import { toast } from 'sonner';

import {
  useNotificationPreferences,
  useRestoreNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@/features/notifications/use-notifications';
import { ApiRequestError } from '@/lib/api';
import { useMe } from '@/platform/auth/use-me';
import {
  ProfileUnsavedBlocker,
  ProfileUnsavedBlockerHost,
} from '@/platform/features/profile/profile-unsaved-blocker';
import {
  useUpdateOwnBirthday,
  useUpdateOwnProfile,
} from '@/platform/features/users/use-own-profile';
import {
  applyProductNotificationPreset,
  type NotificationPresetId,
} from '@/platform/lib/profile-notification-presets';
import { shouldShowNotificationProductInProfile } from '@/platform/lib/profile-notification-groups';
import { useProfileInvalidationHold } from '@/platform/lib/use-invalidation-hold';

const TIMEZONES = ['Europe/Berlin'] as const;

function timezoneFromProfile(profile: OwnProfile): string {
  return TIMEZONES.includes(profile.timezone as (typeof TIMEZONES)[number])
    ? profile.timezone
    : profile.timezone || 'Europe/Berlin';
}

function publicFieldsFromProfile(profile: OwnProfile) {
  return {
    siteId: profile.siteId ?? '',
    aboutMe: profile.aboutMe ?? '',
  };
}

function birthdayFieldsFromProfile(profile: OwnProfile) {
  return {
    birthdayMonth: profile.birthdayMonth ? String(profile.birthdayMonth) : '',
    birthdayDay: profile.birthdayDay ? String(profile.birthdayDay) : '',
    showBirthday: profile.showBirthday,
    hiredOn: profile.hiredOn ?? '',
  };
}

type ProfileEditContextValue = {
  profile: OwnProfile;
  phone: string;
  setPhone: (value: string) => void;
  jobTitle: string;
  setJobTitle: (value: string) => void;
  timezone: string;
  setTimezone: (value: string) => void;
  timezoneOptions: string[];
  items: NotificationPreference[];
  preferencesPending: boolean;
  orgEmailEnabled: boolean | undefined;
  preferenceGroups: {
    productCode: NotificationProductCode;
    eventKeys: (typeof NOTIFICATION_EVENT_KEYS)[number][];
  }[];
  patchPreference: (
    eventKey: NotificationPreference['eventKey'],
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) => void;
  applyProductPreset: (productCode: NotificationProductCode, preset: NotificationPresetId) => void;
  patchProductColumn: (
    productCode: NotificationProductCode,
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) => void;
  siteId: string;
  setSiteId: (value: string) => void;
  aboutMe: string;
  setAboutMe: (value: string) => void;
  birthdayMonth: string;
  setBirthdayMonth: (value: string) => void;
  birthdayDay: string;
  setBirthdayDay: (value: string) => void;
  showBirthday: boolean;
  setShowBirthday: (value: boolean) => void;
  hiredOn: string;
  setHiredOn: (value: string) => void;
  profileFieldsDirty: boolean;
  overviewDirty: boolean;
  preferencesDirty: boolean;
  savingProfile: boolean;
  savingOverview: boolean;
  savingPreferences: boolean;
  saveOverviewSettings: (event?: FormEvent) => Promise<void>;
  saveTimezoneFields: () => Promise<void>;
  savePreferencesOnly: (event?: FormEvent) => Promise<void>;
  followsTenantStandard: boolean;
  hasPersonalPreferences: boolean;
  restorePreferences: () => Promise<void>;
};

const ProfileEditContext = createContext<ProfileEditContextValue | null>(null);

export function ProfileEditProvider({
  profile,
  children,
}: {
  profile: OwnProfile;
  children: ReactNode;
}) {
  const me = useMe();
  const saveProfile = useUpdateOwnProfile();
  const saveBirthday = useUpdateOwnBirthday();
  const preferences = useNotificationPreferences(true);
  const savePreferences = useUpdateNotificationPreferences();
  const restoreSavedPreferences = useRestoreNotificationPreferences();
  const profilePhone = profile.phone ?? '';
  const profileJobTitle = profile.jobTitle ?? '';
  const profileTimezone = timezoneFromProfile(profile);
  const profilePublic = publicFieldsFromProfile(profile);
  const profileBirthday = birthdayFieldsFromProfile(profile);
  const [phone, setPhone] = useState(profilePhone);
  const [jobTitle, setJobTitle] = useState(profileJobTitle);
  const [timezone, setTimezone] = useState(profileTimezone);
  const [siteId, setSiteId] = useState(profilePublic.siteId);
  const [aboutMe, setAboutMe] = useState(profilePublic.aboutMe);
  const [birthdayMonth, setBirthdayMonth] = useState(profileBirthday.birthdayMonth);
  const [birthdayDay, setBirthdayDay] = useState(profileBirthday.birthdayDay);
  const [showBirthday, setShowBirthday] = useState(profileBirthday.showBirthday);
  const [hiredOn, setHiredOn] = useState(profileBirthday.hiredOn);
  const [draft, setDraft] = useState<NotificationPreference[] | null>(null);
  const [syncedProfileFields, setSyncedProfileFields] = useState({
    phone: profilePhone,
    jobTitle: profileJobTitle,
    timezone: profileTimezone,
    public: profilePublic,
    birthday: profileBirthday,
  });

  if (
    syncedProfileFields.phone !== profilePhone ||
    syncedProfileFields.jobTitle !== profileJobTitle ||
    syncedProfileFields.timezone !== profileTimezone ||
    syncedProfileFields.public.siteId !== profilePublic.siteId ||
    syncedProfileFields.public.aboutMe !== profilePublic.aboutMe ||
    syncedProfileFields.birthday.birthdayMonth !== profileBirthday.birthdayMonth ||
    syncedProfileFields.birthday.birthdayDay !== profileBirthday.birthdayDay ||
    syncedProfileFields.birthday.showBirthday !== profileBirthday.showBirthday ||
    syncedProfileFields.birthday.hiredOn !== profileBirthday.hiredOn
  ) {
    setSyncedProfileFields({
      phone: profilePhone,
      jobTitle: profileJobTitle,
      timezone: profileTimezone,
      public: profilePublic,
      birthday: profileBirthday,
    });
    setPhone(profilePhone);
    setJobTitle(profileJobTitle);
    setTimezone(profileTimezone);
    setSiteId(profilePublic.siteId);
    setAboutMe(profilePublic.aboutMe);
    setBirthdayMonth(profileBirthday.birthdayMonth);
    setBirthdayDay(profileBirthday.birthdayDay);
    setShowBirthday(profileBirthday.showBirthday);
    setHiredOn(profileBirthday.hiredOn);
  }

  const savedPhone = profile.phone ?? '';
  const savedJobTitle = profile.jobTitle ?? '';
  const savedPublic = publicFieldsFromProfile(profile);
  const savedBirthday = birthdayFieldsFromProfile(profile);
  const phoneDirty = phone.trim() !== savedPhone.trim();
  const jobTitleDirty = jobTitle.trim() !== savedJobTitle.trim();
  const timezoneDirty = timezone !== profile.timezone;
  const publicDirty =
    siteId !== savedPublic.siteId || aboutMe.trim() !== savedPublic.aboutMe.trim();
  const birthdayDirty =
    birthdayMonth !== savedBirthday.birthdayMonth ||
    birthdayDay !== savedBirthday.birthdayDay ||
    showBirthday !== savedBirthday.showBirthday ||
    hiredOn !== savedBirthday.hiredOn;
  const overviewDirty = phoneDirty || jobTitleDirty || publicDirty || birthdayDirty;
  const profileFieldsDirty =
    phoneDirty || jobTitleDirty || timezoneDirty || publicDirty || birthdayDirty;
  const preferencesDirty = draft !== null;
  useProfileInvalidationHold(profileFieldsDirty || preferencesDirty);

  const items = draft ?? preferences.data?.items ?? [];
  const timezoneOptions = TIMEZONES.includes(timezone as (typeof TIMEZONES)[number])
    ? [...TIMEZONES]
    : [timezone, ...TIMEZONES];

  const visibleEventKeys = NOTIFICATION_EVENT_KEYS.filter((eventKey) =>
    isNotificationEventVisible(eventKey, me.data?.enabledModules ?? APP_MODULE_CODES),
  );
  const sessionProducts = new Set<string>(me.data?.products ?? []);
  const preferenceGroups = Object.values(NotificationProductCode)
    .filter((productCode) =>
      shouldShowNotificationProductInProfile(productCode, {
        sessionProducts,
      }),
    )
    .map((productCode) => ({
      productCode,
      eventKeys: visibleEventKeys.filter(
        (eventKey) => NOTIFICATION_EVENT_PRODUCT[eventKey] === productCode,
      ),
    }))
    .filter((group) => group.eventKeys.length > 0);

  function patchPreference(
    eventKey: NotificationPreference['eventKey'],
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) {
    setDraft((current) => {
      const base = current ?? preferences.data?.items ?? [];
      return base.map((item) => {
        if (item.eventKey !== eventKey) {
          return item;
        }
        if (field === 'email' && item.emailLocked && value === false) {
          return item;
        }
        if (item.channelsLocked) {
          return item;
        }
        return { ...item, [field]: value };
      });
    });
  }

  function productEventKeys(productCode: NotificationProductCode): NotificationEventKey[] {
    const group = preferenceGroups.find((entry) => entry.productCode === productCode);
    return group?.eventKeys ?? [];
  }

  function applyProductPreset(productCode: NotificationProductCode, preset: NotificationPresetId) {
    const keys = productEventKeys(productCode);
    if (keys.length === 0) {
      return;
    }
    setDraft((current) => {
      const base = current ?? preferences.data?.items ?? [];
      return applyProductNotificationPreset(base, productCode, keys, preset);
    });
  }

  function patchProductColumn(
    productCode: NotificationProductCode,
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) {
    const touch = new Set(productEventKeys(productCode));
    if (touch.size === 0) {
      return;
    }
    setDraft((current) => {
      const base = current ?? preferences.data?.items ?? [];
      return base.map((item) => {
        if (!touch.has(item.eventKey)) {
          return item;
        }
        if (field === 'email' && item.emailLocked && value === false) {
          return item;
        }
        if (item.channelsLocked) {
          return item;
        }
        return { ...item, [field]: value };
      });
    });
  }

  function discardUnsavedChanges() {
    setPhone(profile.phone ?? '');
    setJobTitle(profile.jobTitle ?? '');
    setTimezone(timezoneFromProfile(profile));
    const nextPublic = publicFieldsFromProfile(profile);
    setSiteId(nextPublic.siteId);
    setAboutMe(nextPublic.aboutMe);
    const nextBirthday = birthdayFieldsFromProfile(profile);
    setBirthdayMonth(nextBirthday.birthdayMonth);
    setBirthdayDay(nextBirthday.birthdayDay);
    setShowBirthday(nextBirthday.showBirthday);
    setHiredOn(nextBirthday.hiredOn);
    setDraft(null);
  }

  async function saveOverviewSettings(event?: FormEvent) {
    event?.preventDefault();
    if (!overviewDirty) {
      return;
    }
    try {
      if (phoneDirty || jobTitleDirty || publicDirty) {
        await saveProfile.mutateAsync({
          ...(phoneDirty ? { phone: phone.trim() === '' ? null : phone.trim() } : {}),
          ...(jobTitleDirty ? { jobTitle: jobTitle.trim() === '' ? null : jobTitle.trim() } : {}),
          ...(publicDirty
            ? {
                siteId: siteId || null,
                aboutMe: aboutMe.trim() === '' ? null : aboutMe.trim(),
              }
            : {}),
          locale: 'de-DE',
        });
      }
      if (birthdayDirty) {
        const month = birthdayMonth ? Number(birthdayMonth) : null;
        const day = birthdayDay ? Number(birthdayDay) : null;
        await saveBirthday.mutateAsync({
          birthdayMonth: month,
          birthdayDay: day,
          showBirthday: month && day ? showBirthday : false,
          hiredOn: hiredOn || null,
        });
      }
      toast.success('Profil gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Profil konnte nicht gespeichert werden.',
      );
      throw error;
    }
  }

  async function saveTimezoneFields() {
    try {
      await saveProfile.mutateAsync({
        phone: profile.phone,
        locale: 'de-DE',
        timezone,
      });
      toast.success('Profil gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Profil konnte nicht gespeichert werden.',
      );
      throw error;
    }
  }

  async function savePreferencesOnly(event?: FormEvent) {
    event?.preventDefault();
    if (items.length === 0) {
      return;
    }
    try {
      await savePreferences.mutateAsync({ items });
      setDraft(null);
      toast.success('Mitteilungen gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Mitteilungen konnten nicht gespeichert werden.',
      );
    }
  }

  async function restorePreferences() {
    try {
      await restoreSavedPreferences.mutateAsync();
      setDraft(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Mitteilungen konnten nicht wiederhergestellt werden.',
      );
    }
  }

  const value: ProfileEditContextValue = {
    profile,
    phone,
    setPhone,
    jobTitle,
    setJobTitle,
    timezone,
    setTimezone,
    timezoneOptions,
    items,
    preferencesPending: preferences.isPending,
    orgEmailEnabled: preferences.data?.orgEmailEnabled,
    preferenceGroups,
    patchPreference,
    applyProductPreset,
    patchProductColumn,
    siteId,
    setSiteId,
    aboutMe,
    setAboutMe,
    birthdayMonth,
    setBirthdayMonth,
    birthdayDay,
    setBirthdayDay,
    showBirthday,
    setShowBirthday,
    hiredOn,
    setHiredOn,
    profileFieldsDirty,
    overviewDirty,
    preferencesDirty,
    savingProfile: saveProfile.isPending,
    savingOverview: saveProfile.isPending || saveBirthday.isPending,
    savingPreferences: savePreferences.isPending,
    saveOverviewSettings,
    saveTimezoneFields,
    savePreferencesOnly,
    followsTenantStandard: preferences.data?.followsTenantStandard === true,
    hasPersonalPreferences: preferences.data?.hasPersonalPreferences === true,
    restorePreferences,
  };

  return (
    <ProfileEditContext.Provider value={value}>
      {children}
      <ProfileUnsavedBlocker
        when={profileFieldsDirty || preferencesDirty}
        onDiscard={discardUnsavedChanges}
      />
      <ProfileUnsavedBlockerHost />
    </ProfileEditContext.Provider>
  );
}

export function useProfileEdit(): ProfileEditContextValue {
  const context = useContext(ProfileEditContext);
  if (!context) {
    throw new Error('useProfileEdit must be used within ProfileEditProvider');
  }
  return context;
}
